import * as React from "react"
import { Box, } from "@mui/material"
import { AXIOS_API_INSTANCE } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop";
import { useSnackbar } from "notistack";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { moneyFormat } from "../../Utils"

export default function ServiceServiceKitReport({ startDatetime, endDatetime, selectedLocations }) {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = React.useState(false)
    const [serviceKits, setServiceKits] = React.useState([])
    const [isLoadingEfficiency, setIsLoadingEfficiency] = React.useState(false)
    const [serviceKitEfficiencyData, setServiceKitEfficiencyData] = React.useState({})

    const getServiceKits = () => {
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("servicekits", {
                params: {
                    order_by: 'name',
                    order_by_direction: 'asc',
                },
            }).then((servicekits) => {
                setServiceKits(servicekits.data.data.map((x) => {
                    return {
                        ...x,
                        dollars_per_hour_expected: (x.default_flat_rate / (x.default_minutes_worked / 60)) + x.hourly_rate
                    }
                }))
                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to load data <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const getServiceEfficiency = (start_datetime, end_datetime, selectedLocations) => {
        setIsLoadingEfficiency(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("reports/serviceorder_service_efficiency", {
                params: {
                    start_datetime: start_datetime,
                    end_datetime: end_datetime.add(1, 'day'),
                    location_ids: selectedLocations.map((x) => x.id),
                },
            }).then((report) => {
                const kit_data = {}
                Object.keys(report.data.data).forEach((x) => {
                    const service = report.data.data[x]
                    if (!kit_data[service.service_kit_id]) {
                        kit_data[service.service_kit_id] = {
                            count: 1,
                            time_worked: service.time_frame_minutes_worked,
                            all_worked_time: service.total_time,
                            total_billed: service.official_flat_rate + (report.data.data[x].official_hourly_rate * report.data.data[x].official_minutes_worked / 60),
                        }
                    } else {
                        kit_data[service.service_kit_id].count += 1
                        kit_data[service.service_kit_id].time_worked += service.time_frame_minutes_worked
                        kit_data[service.service_kit_id].all_worked_time += service.total_time
                        kit_data[service.service_kit_id].total_billed += service.official_flat_rate + (report.data.data[x].official_hourly_rate * report.data.data[x].official_minutes_worked / 60)
                    }
                })
                Object.keys(kit_data).forEach((x) => {
                    kit_data[x].dollars_billed = (kit_data[x].time_worked / kit_data[x].all_worked_time) * kit_data[x].total_billed;
                    kit_data[x].dollars_per_hour = kit_data[x].dollars_billed / (kit_data[x].time_worked / 60);
                })
                console.log(kit_data)
                setServiceKitEfficiencyData(kit_data)
                setIsLoadingEfficiency(false)
            }).catch((error) => {
                setIsLoadingEfficiency(false)
                enqueueSnackbar(`Failed to load data <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }
    console.log(serviceKits)

    React.useEffect(() => {
        getServiceKits()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
        getServiceEfficiency(startDatetime, endDatetime, selectedLocations)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDatetime, endDatetime, selectedLocations])

    return (
        <React.Fragment>
            <Box>
                <LoadingBackdropIndeterminate
                    open={isLoading || isLoadingEfficiency}
                    text="Loading Service Kit Report"
                />
                {!isLoading && !isLoadingEfficiency &&
                    <Box>
                        <p>Showing the performance of each service kit over the time period. Shown below is the amount of time worked in the time period. If work was done on a service kit on the same service order outside of the time frame it is not included in the calculation. Only services which have been invoiced are included in the calculation.</p>
                        <DataGrid
                            rows={serviceKits}
                            slots={{ toolbar: GridToolbar }}
                            loading={isLoading || isLoadingEfficiency}
                            columns={[
                                {
                                    field: "name",
                                    headerName: "Service Kit",
                                    minWidth: 235,
                                    flex: 1,
                                    editable: false,
                                    sortable: true,
                                },
                                {
                                    field: "time_worked",
                                    headerName: "Time Worked",
                                    flex: 1,
                                    editable: false,
                                    sortable: true,
                                    valueGetter: (value, row) => serviceKitEfficiencyData[row.id]?.time_worked || 0,
                                    valueFormatter: (value) => `${(value / 60).toFixed(2)} hrs`
                                },
                                {
                                    field: "dollars_billed",
                                    headerName: "$ Billed",
                                    flex: 1,
                                    editable: false,
                                    sortable: true,
                                    valueGetter: (value, row) => serviceKitEfficiencyData[row.id]?.dollars_billed || 0,
                                    valueFormatter: (value) => `$${moneyFormat(value, true)}`
                                },
                                {
                                    field: "dollars_per_hour",
                                    headerName: "$ Billed / hr",
                                    flex: 1,
                                    editable: false,
                                    sortable: true,
                                    valueGetter: (value, row) => serviceKitEfficiencyData[row.id]?.dollars_per_hour,
                                    valueFormatter: (value) => `$${moneyFormat(value, true)} / hr`
                                },
                                {
                                    field: "dollars_per_hour_expected",
                                    headerName: "Expected $ / hr",
                                    flex: 1,
                                    editable: false,
                                    sortable: true,
                                    valueFormatter: (value) => `$${moneyFormat(value, true)} / hr`
                                },
                                {
                                    field: "efficiency",
                                    headerName: "Efficiency",
                                    flex: 1,
                                    editable: false,
                                    sortable: true,
                                    valueGetter: (value, row) => {
                                        const expected = isFinite(row.dollars_per_hour_expected) ? row.dollars_per_hour_expected : NaN
                                        const actual = serviceKitEfficiencyData[row.id]?.dollars_per_hour ?? NaN
                                        const val = actual / expected
                                        if (isFinite(val)) {
                                            return val - 1
                                        } else {
                                            return NaN
                                        }
                                    },
                                    renderCell: (params) => {
                                        if (!isFinite(params.value)) {
                                            return (<b><span>-%</span></b>)
                                        } else if (params.value > 0) {
                                            return (<b><span style={{ color: 'green' }}>+{(params.value * 100).toFixed(1)}%</span></b>)
                                        } else {
                                            return (<b><span style={{ color: 'red' }}>{(params.value * 100).toFixed(1)}%</span></b>)
                                        }
                                    },
                                    sortComparator: (v1, v2) => {
                                        if (isNaN(v1) && isNaN(v2)) {
                                            return 0
                                        }
                                        if (isNaN(v1)) {
                                            return -1
                                        }
                                        if (isNaN(v2)) {
                                            return 1
                                        }
                                        return v1 - v2
                                    }
                                },
                            ]}
                            autoHeight={true}
                            pagination={true}
                            pageSize={100}
                            disableRowSelectionOnClick
                            disablep
                        />
                    </Box>
                }
            </Box>
        </React.Fragment >
    )
}
